import type { SoundName } from '../types';

/**
 * Audio Cache - IndexedDB storage for audio files
 *
 * Caches decoded audio ArrayBuffers with SHA-256 hash validation
 * to avoid re-downloading unchanged files.
 */

const DB_NAME = 'emdr-audio-cache';
const DB_VERSION = 1;
const STORE_NAME = 'sounds';

export interface CachedSound {
  soundName: SoundName;
  arrayBuffer: ArrayBuffer;
  fileHash: string;
  cachedAt: number;
  fileSize: number;
  url: string;
}

export interface CacheStats {
  soundName: SoundName;
  size: number;
  cachedAt: number;
  hash: string;
}

/**
 * Open IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'soundName' });
        console.log('Created IndexedDB object store:', STORE_NAME);
      }
    };
  });
}

/**
 * Get cached sound from IndexedDB
 */
export async function getCachedSound(soundName: SoundName): Promise<CachedSound | null> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(soundName);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(new Error(`Failed to read ${soundName} from cache`));
      };
    });
  } catch (error) {
    console.warn(`Cache read failed for ${soundName}:`, error);
    return null; // Fallback to network
  }
}

/**
 * Store sound in IndexedDB cache
 */
export async function setCachedSound(soundName: SoundName, data: CachedSound): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(data);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error(`Failed to cache ${soundName}`));
      };
    });
  } catch (error) {
    console.warn(`Cache write failed for ${soundName}:`, error);
    // Don't throw - caching is optional
  }
}

/**
 * Clear all cached sounds
 */
export async function clearCache(): Promise<void> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('✓ Audio cache cleared');
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to clear cache'));
      };
    });
  } catch (error) {
    console.error('Failed to clear cache:', error);
    throw error;
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<CacheStats[]> {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const cached = request.result as CachedSound[];
        const stats = cached.map((item) => ({
          soundName: item.soundName,
          size: item.fileSize,
          cachedAt: item.cachedAt,
          hash: item.fileHash.substring(0, 12) + '...',
        }));
        resolve(stats);
      };

      request.onerror = () => {
        reject(new Error('Failed to get cache stats'));
      };
    });
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return [];
  }
}

/**
 * Check if IndexedDB is available
 */
export function isCacheAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

/**
 * Get total cache size in bytes
 */
export async function getTotalCacheSize(): Promise<number> {
  const stats = await getCacheStats();
  return stats.reduce((total, item) => total + item.size, 0);
}
